
import { Button } from "@/components/ui/button";
import { Sprout, Play } from "lucide-react";
import { VideoPostGrid } from "./VideoPostGrid";
import { PostCard } from "./PostCard";
import { Post } from "@/hooks/posts/types";
import { LazyTimeLapse } from "../LazyTimeLapse";

interface CropSectionProps {
  crop: string;
  posts: Post[];
  videoPostsCount: number;
  onTimelapseClick: (crop: string) => void;
  onPostClick: (post: Post) => void;
  onEditPost?: (post: Post) => void;
  onDeletePost?: (postId: string) => void;
  showActions?: boolean;
}

export const CropSection = ({
  crop,
  posts,
  videoPostsCount,
  onTimelapseClick,
  onPostClick,
  onEditPost,
  onDeletePost,
  showActions = false
}: CropSectionProps) => {
  const videoPosts = posts.filter(post => post.isVideo);
  const nonVideoPosts = posts.filter(post => !post.isVideo);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between border-b border-green-100 pb-2">
        <div className="flex items-center gap-2">
          <Sprout className="h-5 w-5 text-green-600" />
          <h4 className="text-lg font-medium text-green-800">{crop}</h4>
          <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-sm">
            {posts.length} post{posts.length !== 1 ? 's' : ''}
          </span>
        </div>
        {videoPostsCount > 0 && (
          <LazyTimeLapse
            crop={crop}
            posts={posts}
            onClose={() => {}} // Will be handled by the lazy loader
            trigger={
              <Button
                variant="outline"
                size="sm"
                className="text-green-600 border-green-600 hover:bg-green-50 flex items-center gap-1"
              >
                <Play size={14} />
                Timelapse ({videoPostsCount})
              </Button>
            }
          />
        )}
      </div>

      {videoPosts.length > 0 && (
        <div className="mb-4">
          <h5 className="text-sm font-medium text-gray-600 mb-2 flex items-center gap-1">
            <Play size={14} />
            Video Posts
          </h5>
          <VideoPostGrid
            posts={videoPosts}
            onPostClick={onPostClick}
            onEdit={onEditPost}
            onDelete={onDeletePost}
            showActions={showActions}
          />
        </div>
      )}

      {nonVideoPosts.length > 0 && (
        <div className="grid gap-4">
          {nonVideoPosts.map((post) => (
            <PostCard
              key={post.id}
              post={post}
              onEdit={onEditPost}
              onDelete={onDeletePost}
              showActions={showActions}
            />
          ))}
        </div>
      )}
    </div>
  );
};
